import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { isBlockedPathForUpdatePrompt } from "@/lib/sw-register";
import { hardRefreshToLatest, isUpdateReady } from "@/lib/app-version";

/**
 * Shows an "Update available" toast — but ONLY when a genuinely newer build has
 * been deployed (running build id != deployed build id). The previous version
 * fired whenever a service worker was merely `waiting`, which is also true right
 * after a hard refresh that already loaded the latest code — producing a false,
 * sticky toast that wouldn't go away. The version gate fixes that.
 *
 * Route-gated: never shows on marketing pages, /auth, /reset-password, or
 * /onboarding. Re-checks on route change so an update detected while on a gated
 * page surfaces once the user reaches the dashboard.
 *
 * Refresh uses the same budgeted hard-refresh path as the profile menu so
 * mobile / PWA taps don't hang on Cache API deletes.
 */
export function SwUpdateToast() {
  const location = useLocation();

  useEffect(() => {
    const handleUpdate = async () => {
      if (isBlockedPathForUpdatePrompt()) return;
      if (!(await isUpdateReady(true))) return; // force a fresh check on a real event
      showToast();
    };

    window.addEventListener("sw-update-available", handleUpdate);
    return () => window.removeEventListener("sw-update-available", handleUpdate);
  }, []);

  useEffect(() => {
    if (isBlockedPathForUpdatePrompt(location.pathname)) return;
    if (!("serviceWorker" in navigator)) return;

    let cancelled = false;
    (async () => {
      const reg = await navigator.serviceWorker.getRegistration().catch(() => null);
      if (cancelled || !reg?.waiting) return;
      if (!(await isUpdateReady())) return;
      showToast();
    })();

    return () => { cancelled = true; };
  }, [location.pathname]);

  return null;
}

let toastShown = false;
function showToast() {
  // Guard against the event handler + route-change re-check both firing for the
  // same waiting SW within one session.
  if (toastShown) return;
  toastShown = true;
  toast("Update available", {
    description: "A new version is ready. Tap to refresh.",
    // Finite so it can't sit on screen forever; the route-change re-check
    // resurfaces it (still version-gated) if the user navigates while behind.
    duration: 30_000,
    onDismiss: () => { toastShown = false; },
    onAutoClose: () => { toastShown = false; },
    action: {
      label: "Refresh",
      onClick: () => { void hardRefreshToLatest(); },
    },
  });
}
